/**
 * 书架卡片模板与计数辅助函数（同步）
 */

export function getCollectionBookCount(state, collectionId) {
  const existingBookIds = new Set((state.books || []).map(b => String(b.id)));
  const ids = state.collectionMembership?.[collectionId] || [];
  if (!Array.isArray(ids)) return 0;
  return ids.filter(id => existingBookIds.has(String(id))).length;
}

export function getBooklistBookCount(state, booklistId) {
  const existingBookIds = new Set((state.books || []).map(b => String(b.id)));
  const ids = state.booklistMembership?.[booklistId] || [];
  if (!Array.isArray(ids)) return 0;
  return ids.filter(id => existingBookIds.has(String(id))).length;
}

export function getCollectionPreviewBooks(state, collectionId, maxCount = 4) {
  const ids = Array.isArray(state.collectionMembership?.[collectionId])
    ? state.collectionMembership[collectionId].map(String)
    : [];
  if (ids.length === 0) return [];

  const bookMap = new Map((state.books || []).map(b => [String(b.id), b]));
  const withCover = [];
  const withoutCover = [];
  for (const id of ids) {
    const book = bookMap.get(id);
    if (!book) continue;
    if (book.cover) {
      withCover.push(book);
    } else {
      withoutCover.push(book);
    }
    if (withCover.length >= maxCount) break;
  }
  return [...withCover, ...withoutCover].slice(0, maxCount);
}

export function buildCollectionCoverGridHtml(state, collectionId, maxItems = 12) {
  const books = getCollectionPreviewBooks(state, collectionId, maxItems);
  const filled = books.map(book => {
    const cover = book?.cover;
    if (!cover) return '';
    return `<div class="collection-cover-cell"><img src="${cover}" alt="" loading="lazy"></div>`;
  }).filter(Boolean);

  if (filled.length === 0) return '';

  return `<div class="collection-cover-grid collection-cover-grid--cols3">${filled.join('')}</div>`;
}

export function createCollectionCardHtml(state, escapeHtml, collection) {
  const count = getCollectionBookCount(state, collection.id);
  return `
    <article class="collection-card" data-collection-id="${escapeHtml(collection.id)}">
      <div class="collection-cover">
        ${buildCollectionCoverGridHtml(state, collection.id, 12)}
      </div>
      <div class="collection-info">
        <h3 class="collection-title">${escapeHtml(collection.name)}</h3>
        <div class="collection-meta">
          <span class="collection-count">${count} 本书</span>
        </div>
      </div>
      <div class="collection-actions">
        <button class="book-action-btn collection-edit-btn" data-collection="${escapeHtml(collection.id)}" title="编辑">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
          </svg>
        </button>
      </div>
    </article>
  `;
}

export function createCollectionCurrentCardHtml(state, escapeHtml, collection) {
  const count = getCollectionBookCount(state, collection.id);
  return `
    <article class="collection-card collection-card--current" data-action="current" data-collection-id="${escapeHtml(collection.id)}">
      <div class="collection-cover">
        ${buildCollectionCoverGridHtml(state, collection.id, 12)}
      </div>
      <div class="collection-info">
        <h3 class="collection-title">${escapeHtml(collection.name)}</h3>
        <div class="collection-meta">
          <span class="collection-count">${count} 本书</span>
        </div>
      </div>
      <div class="collection-actions">
        <button class="book-action-btn collection-edit-btn" data-collection="${escapeHtml(collection.id)}" title="编辑">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
          </svg>
        </button>
      </div>
    </article>
  `;
}

export function createCollectionActionCardHtml(escapeHtml, action, label, subtitle = '', meta = '') {
  const icon =
    action === 'add'
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14"/></svg>`
      : action === 'manage'
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 18l-6-6 6-6"/></svg>`;

  const subtitleHtml = subtitle ? `<div class="collection-action-subtitle">${escapeHtml(subtitle)}</div>` : '';
  const metaHtml = meta ? `<div class="collection-action-meta">${escapeHtml(meta)}</div>` : '';
  const extraClass = action === 'back' ? ' collection-card--action-back' : '';
  return `
    <article class="collection-card collection-card--action${extraClass}" data-action="${escapeHtml(action)}">
      <div class="collection-action-content">
        <div class="collection-action-icon">${icon}</div>
        <div class="collection-action-label">${escapeHtml(label)}</div>
        ${subtitleHtml}
        ${metaHtml}
      </div>
    </article>
  `;
}

export function createCollectionListItemHtml(state, escapeHtml, collection) {
  const count = getCollectionBookCount(state, collection.id);
  return `
    <div class="collection-list-item" data-collection-id="${escapeHtml(collection.id)}">
      <div class="collection-list-cover">
        ${buildCollectionCoverGridHtml(state, collection.id, 12)}
      </div>
      <div class="collection-list-info">
        <h3>${escapeHtml(collection.name)}</h3>
        <p>${count} 本书</p>
      </div>
      <button class="book-action-btn collection-edit-btn" data-collection="${escapeHtml(collection.id)}" title="编辑">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
        </svg>
      </button>
    </div>
  `;
}

export function createCollectionCurrentListItemHtml(state, escapeHtml, collection) {
  const count = getCollectionBookCount(state, collection.id);
  return `
    <div class="collection-list-item collection-list-item--current" data-action="current" data-collection-id="${escapeHtml(collection.id)}">
      <div class="collection-list-cover">
        ${buildCollectionCoverGridHtml(state, collection.id, 12)}
      </div>
      <div class="collection-list-info">
        <h3>${escapeHtml(collection.name)}</h3>
        <p>${count} 本书</p>
      </div>
      <button class="book-action-btn collection-edit-btn" data-collection="${escapeHtml(collection.id)}" title="编辑">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
        </svg>
      </button>
    </div>
  `;
}

export function createCollectionActionListItemHtml(escapeHtml, action, label, subtitle = '', meta = '') {
  const icon =
    action === 'add'
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14"/></svg>`
      : action === 'manage'
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 18l-6-6 6-6"/></svg>`;

  const details = [subtitle, meta].filter(Boolean).join(' · ');
  const extraClass = action === 'back' ? ' collection-list-item--action-back' : '';
  return `
    <div class="collection-list-item collection-list-item--action${extraClass}" data-action="${escapeHtml(action)}">
      <div class="collection-list-action-icon">${icon}</div>
      <div class="collection-list-info">
        <h3>${escapeHtml(label)}</h3>
        <p>${escapeHtml(details)}</p>
      </div>
    </div>
  `;
}

export function createBooklistActionCardHtml(escapeHtml, action, label, subtitle = '', meta = '') {
  const icon =
    action === 'add'
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14"/></svg>`
      : action === 'manage'
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 18l-6-6 6-6"/></svg>`;

  const subtitleHtml = subtitle ? `<div class="collection-action-subtitle">${escapeHtml(subtitle)}</div>` : '';
  const metaHtml = meta ? `<div class="collection-action-meta">${escapeHtml(meta)}</div>` : '';
  return `
    <article class="book-card booklist-card booklist-card--action" data-action="${escapeHtml(action)}">
      <div class="collection-action-content">
        <div class="collection-action-icon">${icon}</div>
        <div class="collection-action-label">${escapeHtml(label)}</div>
        ${subtitleHtml}
        ${metaHtml}
      </div>
    </article>
  `;
}

export function createBooklistActionListItemHtml(escapeHtml, action, label, subtitle = '', meta = '') {
  const icon =
    action === 'add'
      ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14"/></svg>`
      : action === 'manage'
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 6h16M4 12h16M4 18h16"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 18l-6-6 6-6"/></svg>`;

  const details = [subtitle, meta].filter(Boolean).join(' · ');
  return `
    <div class="book-list-item booklist-list-item booklist-list-item--action" data-action="${escapeHtml(action)}">
      <div class="collection-list-action-icon">${icon}</div>
      <div class="collection-list-info">
        <h3>${escapeHtml(label)}</h3>
        <p>${escapeHtml(details)}</p>
      </div>
    </div>
  `;
}
